import { Collection, Color, DynamicLoader, MixinUtil, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { CaptionLabel } from "../label/CaptionLabel";
import { ClickEvent, ClickListener, Component, DataSourceMode, PrintableEvent } from "../..";
import { CaptionedMixin } from "../../mixins/CaptionedMixin";
import { CaptionedMixinInterface } from "../../mixins/CaptionedMixinInterface";
import { getCurrentDataSourceMode, getRelevantModelRow } from "../../base/ComponentDataLink";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ListenerListDef } from "../../base/ListenerListDef";
import { PrintableLabel } from "../label/PrintableLabel";
import { PrintableMixin } from "../../mixins/PrintableMixin";
import { PrintableMixinInterface } from "../../mixins/PrintableMixinInterface";
import { Image } from "../../components/image/Image";
import { ChangeEvent, ChangeListener } from "../../events/ChangeEvent";
import { Event } from "../../events/Event";
import { KeyEvent } from "../../events/KeyEvent";
import { Label } from "../label/Label";
import { LabelProps } from "../label/LabelProps";
import { CheckboxPropDefinitions, CheckboxProps } from "./CheckboxProps";
import { CheckboxStyles } from "./CheckboxStyles";
import { ComponentPropDefinition } from "../../base/ComponentProps";

const _changeListenerDef: ListenerListDef = { listName: "_changeListeners" };

export class Checkbox extends Component implements CheckboxProps {
    private _image: Image;
    private printableLabel: PrintableLabel;
    private _checkColor: Color;
    public valueChecked: any = "Y";
    public valueUnchecked: any = "N";
    private _allowIndeterminate: boolean = false;
    private _checked: boolean;
    private _focusable: boolean;
    private _defaultDataValue: boolean;
    private userClickListener: ClickListener = (event: ClickEvent) => {
        this.userClicked(event);
        if (event.target === this.captionLabel && this.focusable) {
            this.focus();
        }
        if (this._designer != null)
            event.shouldAutomaticallyStopPropagation = false;
    }

    constructor(props?: Partial<CheckboxProps>) {
        super("div", props);
        this.captionLabel = new CaptionLabel(this);
        this.captionLabel.setClassIncluded(CheckboxStyles.captionLabel);
        this._element.classList.add(CheckboxStyles.checkBase);
        this._element.appendChild(this.captionLabel._element);
        this._addChild();
        this.checked = false;
        this.setProps(props);
        this.supportsEditSecurity = true;
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._image, "image");
        this.captionLabel?.syncId();
        this.printableLabel?.syncId();
    }

    override get defaultDataValue(): boolean {
        return this._defaultDataValue;
    }

    public set defaultDataValue(value: boolean) {
        this._defaultDataValue = value;
    }

    override setProps(props: Partial<CheckboxProps>) {
        super.setProps(props);
    }

    override getFocusTarget(): HTMLElement {
        return this._image?._element;
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (data == null || this.field == null)
            this.checked = false;
        else {
            let value = (data instanceof ModelRow) ? data.get(this.field) : data[this.field];
            if (typeof value === "string") {
                if (this.valueChecked == null)
                    value = value === "Y" || value === "true";
                else
                    value = value === this.valueChecked;
            }
            if (this._allowIndeterminate)
                this.checked = value;
            else
                this.checked = value === true;
        }
    }

    get caption(): string {
        return this.super_get_caption();
    }

    set caption(value: string) {
        if (this.captionValueMatches(value) === true) {
            return;
        }
        this.super_set_caption(value);
        this.syncCaption();
    }

    get captionLabelProps(): Partial<LabelProps> {
        return {
            marginLeft: 3,
            paddingLeft: 0
        };
    }

    syncCaption() {
        this.captionLabel.visible = !StringUtil.isEmptyString(this.caption);
        if (this.printable === true) {
            this.captionLabel.text = this.caption + ":";
            this.captionLabel.removeClickListener(this.userClickListener);
        } else {
            this.captionLabel.text = this.getPrefixedCaption();
            this.captionLabel.addClickListener(this.userClickListener);
        }
    }

    private userClicked(event: Event) {
        if (this.enabled && this._designer == null)
            this.userToggled(event);
    }

    protected userToggled(event: Event) {
        if (this._allowIndeterminate && this.checked === false)
            this.checked = undefined;
        else
            this._internalSetChecked(!this.checked, event);
    }

    public toggleChecked(): boolean {
        this.checked = !this.checked;
        return this.checked;
    }

    get checked() {
        return this._checked;
    }

    set checked(value: boolean) {
        this._internalSetChecked(value, null);
    }

    get checkSize(): number {
        return this._image?.minHeight as number;
    }

    set checkSize(value: number) {
        this._image.minHeight = value;
        this._image.minWidth = value;
    }

    get valueAsString(): string {
        return this.checked.toString();
    }

    set valueAsString(value: string) {
        this.checked = value === "true";
    }

    private _internalSetChecked(value: boolean, originatingEvent: Event) {
        if (this._checked === value)
            return;
        this._checked = value;
        this._internalUpdateBoundData();
        this.storeUserChoiceIfRemembered();
        const changeEvent = new ChangeEvent(this, !value, value, originatingEvent == null ? null : originatingEvent.domEvent);
        this.fireListeners(_changeListenerDef, changeEvent);
        this._displayValue();
    }

    private get defaultCheckColor(): string {
        return this.checked ? "primary" : "strokePrimary";
    }

    get checkColor(): string {
        return this._checkColor ?? this.defaultCheckColor;
    }

    set checkColor(value: string) {
        this._checkColor = value;
        this._addChild();
    }

    get focusable(): boolean {
        return this._focusable != null ? this._focusable : true;
    }

    set focusable(value: boolean) {
        this._focusable = value;
        this._image._element.setAttribute("tabindex", value === true ? "0" : "-1");
    }

    override _applyEnabled(value: boolean): void {
        if (this.__designer != null) {
            return;
        }

        // don't apply disabled styles to main element when printable
        if (value || this.printable === true) {
            this.cursor = this.cursor;
            this.style.opacity = "1";
            this.style.pointerEvents = "";
        } else {
            this.style.opacity = "0.5";
            this.style.cursor = "";
            this.style.pointerEvents = "none";
        }

        this.applyEnabledImageStyles(value);
    }

    private applyEnabledImageStyles(value: boolean) {
        if (this._image != null) {

            if (value) {
                this._image._element.removeAttribute("disabled");
            } else {
                this._image._element.setAttribute("disabled", "true");
            }

            this._image._element.tabIndex = value && this.focusable === true ? 0 : -1;
            this._image.setClassIncluded(CheckboxStyles.image, value);
            this._image.setClassIncluded(CheckboxStyles.imageDisabled, !value);
        }
    }

    get allowIndeterminate(): boolean {
        return this._allowIndeterminate;
    }

    set allowIndeterminate(value: boolean) {
        this._allowIndeterminate = value;
    }

    private _addChild() {
        if (this.printable === true) {
            if (this._image != null)
                this._element.removeChild(this._image._element);
            this._image = null;
            this._addPrintableLabel();
        } else {
            let reattachListeners = false;
            if (this.printableLabel != null) {
                this._element.removeChild(this.printableLabel._element);
                this.printableLabel = null;
                reattachListeners = true;
            }
            this._addImage(reattachListeners);
        }
        this._displayValue();
        this.syncCaption();
        this._syncEnabled();
    }

    private _addPrintableLabel() {
        if (this.printableLabel == null) {
            // Cannot instantiate using class directly, causes circular reference
            const printableLabelClass = DynamicLoader.getClassForPath("components/components/label/PrintableLabel");
            this.printableLabel = new printableLabelClass(this);
            this._element.appendChild(this.printableLabel._element);
        }
    }

    public override getEventTarget(): HTMLElement {
        return this._image?._element;
    }

    private _addImage(reattachListeners: boolean = false) {
        if (this._image == null) {
            this._image = new Image({
                id: this.createInnerComponentId("image"),
                themeKey: "checkbox.image",
                marginRight: 0,
            });

            if (reattachListeners === true)
                this.reattachListeners();
            this._image.addClickListener(this.userClickListener);
            this._image.addKeyDownListener(event => this._imageKeyPressed(event));
            if (this._element.contains(this.captionLabel._element))
                this._element.insertBefore(this._image._element, this.captionLabel._element);
            else
                this._element.appendChild(this._image._element);
        }
    }

    private _imageKeyPressed(event: KeyEvent) {
        if (event.key === ' ' && this.enabled && this.__designer == null) {
            this.userToggled(event);
            event.consume();
        }
    }

    private _displayValue() {
        this.setElementStyleForAutomation();
        if (this.printableLabel != null) {
            if (this.checked === undefined)
                this.printableLabel.text = "Unknown";
            else
                this.printableLabel.text = this.checked ? "Yes" : "No";
        }
        else if (this._image != null) {
            if (this.checked === undefined)
                this._image.setProps({ name: ImageName.checkboxIndeterminate, color: this.checkColor });
            else
                this._image.setProps({ name: this.checked ? ImageName.checkboxChecked : ImageName.checkboxUnchecked, color: this.checkColor });
        }
    }

    private setElementStyleForAutomation() {
        if (this.checked === undefined) {
            this._element.classList.remove(CheckboxStyles.checked);
            this._element.classList.remove(CheckboxStyles.unchecked);
            this._element.classList.add(CheckboxStyles.indeterminate);
        }
        else if (this.checked) {
            this._element.classList.remove(CheckboxStyles.unchecked);
            this._element.classList.remove(CheckboxStyles.indeterminate);
            this._element.classList.add(CheckboxStyles.checked);
        }
        else {
            this._element.classList.remove(CheckboxStyles.checked);
            this._element.classList.remove(CheckboxStyles.indeterminate);
            this._element.classList.add(CheckboxStyles.unchecked);
        }
    }

    addChangeListener(value: ChangeListener): Checkbox {
        return this.addEventListener(_changeListenerDef, value) as Checkbox;
    }

    removeChangeListener(value: ChangeListener) {
        return this.removeEventListener(_changeListenerDef, value) as Checkbox;
    }

    protected _getDefaultEventProp(): string {
        return "onChange";
    }

    private _internalUpdateBoundData() {
        const row = getRelevantModelRow(this);
        const mode = getCurrentDataSourceMode(this);
        this.updateBoundData(row, mode);
    }

    override updateBoundData(row: ModelRow, mode: DataSourceMode) {
        if (this.field != null) {
            if (this.checked == true)
                row?.set(this.field, this.valueChecked, this);
            else if (this.checked === false)
                row?.set(this.field, this.valueUnchecked, this);
            else
                row?.set(this.field, undefined, this);
        }
    }

    override dataSourceModeChanged(mode: DataSourceMode) {
        super.dataSourceModeChanged(mode);
        if (mode === DataSourceMode.SEARCH) {
            this._allowIndeterminate = true;
            this.checked = undefined;
        }
        else
            this._allowIndeterminate = false;
        this.syncPrintable();
    }

    protected _applyPrintable(_value: boolean) {
        this._addChild();
        this.firePrintableListeners(new PrintableEvent(this, this.printableLabel));
    }

    override getPropertyDefinitions() {
        return CheckboxPropDefinitions.getDefinitions();
    }

    override getSearchValues(): string[] {
        const result = [];
        result.push(this.checked ? "Yes" : "No");
        return result;
    }

    override get serializationName() {
        return "checkbox";
    }

    override get properName(): string {
        return "Checkbox";
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "change": { ..._changeListenerDef },
            ...MixinUtil.getMixinListenerDefs(this)
        };
    }

    override getBasicValue(): any {
        return this.checked;
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "checkColor") {
            return this.defaultCheckColor;
        }
        return super.determinePropertyDefaultValue(prop);
    }

    /**
     * For a Checkbox, display the props access label to the right of the caption label.
     */
    displayPropsAccessLabel(label: Label) {
        label.paddingLeft = 0;
        label.marginLeft = 0;
        this._element.appendChild(label._element);
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Checkbox, [ CaptionedMixin, PrintableMixin ]);

// TypeScript interface declaration for mixin integration
export interface Checkbox extends CaptionedMixinInterface, PrintableMixinInterface {
}

ComponentTypes.registerComponentType("checkbox", {
    constructor: Checkbox.prototype.constructor
});
